import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {DEFAULT_IP_RANGE, VALIDATION_ERRORS} from '../constants';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {IpRange} from 'src/openapi/admin/models/IpRange';

const ipValidationRegex = /^((25[0-5]|2[0-4]\d|1?\d?\d)(\.|$)){4}$/;

export const useValidateIPRange = () => {
    const {addError, removeError} = useContext<ValidationContextType>(ValidationContext);

    const validateDefaultIPRange = useCallback((ranges: Array<IpRange>) => {
        let valid = true;
        if (ranges.length === 1 && isDeepEqual({prev: ranges[0], current: DEFAULT_IP_RANGE})) {
            removeError('account-ip-range-0');
        } else {
            valid = false;
        }

        return valid;
    }, [removeError]);

    const validateUnfilledIp = useCallback((range: IpRange, rangeId: number) => {
        let valid = true;

        if ((!range.start || !range.end)) {
            addError({
                code: 'INVALID_IP_IN_RANGE',
                controlId: `account-ip-range-${rangeId}`,
                message: VALIDATION_ERRORS.INVALID_IP,
                priority: 2,
            });
            valid = false;
        }

        return valid;
    }, [addError]);

    const validateIP = useCallback((range: IpRange, rangeId: number) => {
        let valid = true;

        if ((!range.start || !range.end)) {
            removeError(`account-ip-range-${rangeId}`);
            return true;
        }

        if (!ipValidationRegex.test(range.start) || !ipValidationRegex.test(range.end)) {
            addError({
                code: 'INVALID_IP_IN_RANGE',
                controlId: `account-ip-range-${rangeId}`,
                message: VALIDATION_ERRORS.INVALID_IP,
                priority: 2,
            });
            valid = false;
        } else {
            removeError(`account-ip-range-${rangeId}`, ['INVALID_IP_IN_RANGE']);
        }

        return valid;
    }, [addError, removeError]);

    const validateIPRange = useCallback((range: IpRange, rangeId: number) => {
        let valid = true;
        const startFractions = range.start.split('.');
        const endFractions = range.end.split('.');

        if (startFractions.some((start, i) => {
            const startFraction = +start;
            const endFraction = +endFractions[i];

            return endFraction < startFraction;
        })) {
            addError({
                code: 'INVALID_IP_RANGE',
                controlId: `account-ip-range-${rangeId}`,
                message: VALIDATION_ERRORS.INVALID_IP_RANGE,
                priority: 1,
            });
            valid = false;
        } else {
            removeError(`account-ip-range-${rangeId}`, ['INVALID_IP_RANGE']);
        }

        return valid;
    }, [addError, removeError]);

    const validateIPRangeDuplicates = useCallback((ranges: Array<IpRange>, lastCheckedId: number) => {
        let valid = true;

        ranges.forEach((range, rangeId) => {
            if (ranges.filter(comp => range.start === comp.start && range.end === comp.end).length > 1) {
                if (lastCheckedId >= 0) {
                    addError({
                        code: 'DUPLICATED_IP_RANGE',
                        controlId: `account-ip-range-${lastCheckedId}`,
                        message: VALIDATION_ERRORS.DUPLICATED_IP_RANGE,
                    });
                    valid = false;
                }
            } else {
                removeError(`account-ip-range-${rangeId}`, ['DUPLICATED_IP_RANGE']);
            }
        });

        return valid;
    }, [addError, removeError]);

    return {validateDefaultIPRange, validateUnfilledIp, validateIP, validateIPRange, validateIPRangeDuplicates};
};
